import { ArdoqId, ViewIds, ViewSettingsContexts } from '@ardoq/api-types';
import { fetchJson, fetchVoid, Method, toRequestOptions } from './api';

// Current endpoint spec:
// PUT /api/user/{user-id}/view-settings which takes a body of contextId, viewId and settings.
// GET /api/user/{user-id}/view-settings which takes the required query param contextId and the optional query param viewId.
//
// For example:
// GET /api/user/{user-id}/view-settings?contextId=workspace
// GET /api/user/{user-id}/view-settings?contextId=viewpoint&viewId=modernizedBlockDiagram

type FetchViewSettingsForContextArgs = {
  userId: ArdoqId;
  contextId: ViewSettingsContexts;
};
const fetchViewSettingsForContext = ({
  userId,
  contextId,
}: FetchViewSettingsForContextArgs) => {
  const request = new Request(
    `/api/user/${userId}/view-settings?contextId=${contextId}`
  );

  return fetchJson<Record<string, any>>(request);
};

type FetchViewSettingsForViewArgs = FetchViewSettingsForContextArgs & {
  viewId: ViewIds;
};
const fetchViewSettingsForView = ({
  userId,
  contextId,
  viewId,
}: FetchViewSettingsForViewArgs) => {
  const request = new Request(
    `/api/user/${userId}/view-settings?contextId=${contextId}&viewId=${viewId}`
  );

  return fetchJson<Record<string, any>>(request);
};

/**
 * @param contextId is the context for which to fetch view settings,
 * e.g., 'workspaceMode' or 'viewpointMode' or 'mySpecialContext'.
 */
const fetchViewSettings = (
  userId: ArdoqId,
  contextId: ViewSettingsContexts,
  viewId?: ViewIds
) =>
  viewId
    ? fetchViewSettingsForView({ userId, contextId, viewId })
    : fetchViewSettingsForContext({ userId, contextId });

type UpdateViewSettingsForViewArgs = {
  userId: ArdoqId;
  contextId: string;
  viewId: ViewIds;
  settings: Record<string, any>;
};
const updateViewSettingsForView = ({
  userId,
  contextId,
  viewId,
  settings,
}: UpdateViewSettingsForViewArgs) => {
  const body = {
    contextId,
    viewId,
    settings,
  };
  const request = new Request(
    `/api/user/${userId}/view-settings`,
    toRequestOptions(Method.PUT, body)
  );

  return fetchVoid(request);
};

export const viewSettingsApi = {
  fetchViewSettings,
  updateViewSettingsForView,
};
